import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SpinnerLoaderComponent } from '../../../shared/components/spinner-loader/spinner-loader.component';
import { ConfirmService } from '../../../Utils/_services/confirmation-dialog.service';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { IAdminAchievement } from '../../../Utils/_models/achievements.model';
import { AchievementsService } from '../../../Utils/_services/admin/achievements/achievements.service';
import { AchievementFormComponent } from './achievement-form/achievement-form.component';
import { TooltipModule } from 'primeng/tooltip';
import { SearchBoxComponent } from '../../../shared/components/search-box/search-box.component';

@Component({
  selector: 'app-achievements',
  standalone: true,
  imports: [CommonModule, FormsModule, SpinnerLoaderComponent, AchievementFormComponent, TooltipModule, SearchBoxComponent],
  templateUrl: './achievements.component.html',
  styleUrls: ['./achievements.component.css'],
})
export class AchievementsComponent implements OnInit {
  achievements = signal<IAdminAchievement[]>([]);
  allAchievements = signal<IAdminAchievement[]>([]);
  loading = signal(false);
  isModalOpen = signal(false);
  editingAchievement = signal<IAdminAchievement | null>(null);

  searchTerm = signal<string>('');
  includeDeleted = signal<boolean>(false);
  hasSelection = signal<boolean>(false);
  hasDeletedSelection = signal<boolean>(false);
  allSelected = signal<boolean>(false);
  selectedIds: Set<string> = new Set();
  currentSortField = signal<string | null>(null);
  currentSortOrder = signal<'asc' | 'desc' | null>(null);
  originalAchievements = signal<IAdminAchievement[]>([]);
  // Pagination
  currentPage = signal(1);
  totalCount = signal(0);
  pageSizes = [5, 10, 20, 50];
  pageSize = 5;
  totalPages = 0;
  startIndex = 1;
  endIndex = 5;

  constructor(
    private confirmationService: ConfirmService,
    private achievementService: AchievementsService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.fetchAchievements();
  }

  fetchAchievements(): void {
    this.loading.set(true);
    this.achievementService.getAchievements(this.includeDeleted(), this.searchTerm())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          const all = res?.data?.achievements || [];
          this.allAchievements.set(all);
          this.originalAchievements.set([...all]);
          this.totalCount.set(all.length);
          if (this.currentSortField() && this.currentSortOrder()) {
            this.sortAchievementsLocal();
          } else {
            this.updatePagination();
            this.applyPage();
          }
        },
        error: (err) => {
          this.toastr.error(err?.error?.Errors?.message || 'Failed to load achievements');
          this.allAchievements.set([]);
          this.originalAchievements.set([]);
          this.totalCount.set(0);
          this.updatePagination();
          this.applyPage();
        }
      });
  }

  onSearch(term: string): void {
    this.searchTerm.set(term);
    this.currentPage.set(1);
    this.fetchAchievements();
  }

  toggleIncludeDeleted(): void {
    this.includeDeleted.set(!this.includeDeleted());
    this.fetchAchievements();
  }

  onToggleSelectAll(): void {
    const currentState = this.allSelected();
    this.allSelected.set(!currentState);

    if (!currentState) {
      this.achievements().forEach(a => {
        if (a._id) this.selectedIds.add(a._id);
      });
    } else {
      this.selectedIds.clear();
    }
    this.updateSelectionState();
  }

  onToggleSelect(achievement: any): void {
    if (this.selectedIds.has(achievement._id)) {
      this.selectedIds.delete(achievement._id);
    } else {
      this.selectedIds.add(achievement._id);
    }
    this.updateSelectionState();
  }

  updateSelectionState(): void {
    const all = this.achievements();
    this.hasSelection.set(this.selectedIds.size > 0);
    this.allSelected.set(all.length > 0 && all.every(a => a._id && this.selectedIds.has(a._id)));
    this.hasDeletedSelection.set(all.some(a => a._id && this.selectedIds.has(a._id) && !!a.deletedAt));
  }

  isSelected(achievement: any): boolean {
    return this.selectedIds.has(achievement._id);
  }

  async deleteSelectedAchievements(): Promise<void> {
    if (this.selectedIds.size === 0) return;

    const all = this.achievements();
    const selectedAchievements = all.filter(a => a._id && this.selectedIds.has(a._id));
    const deletedSelected = selectedAchievements.filter(a => a.deletedAt);
    const activeSelected = selectedAchievements.filter(a => !a.deletedAt);
    const isRestore = deletedSelected.length > 0;

    const header = isRestore ? 'Restore Achievements' : 'Delete Achievements';
    const bodyHeader = isRestore ? 'Restore Selected Achievements' : 'Delete Selected Achievements';
    const bodySubtitle = isRestore
      ? `You are about to restore ${deletedSelected.length} deleted achievement(s).${activeSelected.length > 0 ? ` ${activeSelected.length} non-deleted achievement(s) will also be deleted.` : ''}`
      : `Are you sure you want to delete ${activeSelected.length} selected achievement(s)? This action cannot be undone.`;
    const yesButtonText =
      deletedSelected.length > 0 && activeSelected.length > 0
        ? 'Apply Changes'
        : isRestore
          ? 'Restore'
          : 'Delete';
    const yesBtnCss = isRestore ? 'btn-primary' : 'btn-danger';

    const confirmed = await this.confirmationService.confirm({
      header,
      bodyHeader,
      bodySubtitle,
      yesButtonText,
      noButtonText: 'Cancel',
      yesBtnCss,
      noBtnCss: 'btn-secondary',
      width: '420px',
    });

    if (confirmed) {
      const ids = Array.from(this.selectedIds);
      this.achievementService.bulkDeleteAchievements(ids).subscribe({
        next: () => {
          this.toastr.success('Achievements deleted successfully');
          this.selectedIds.clear();
          this.updateSelectionState();
          this.fetchAchievements();
        },
        error: (err) => {
          this.toastr.error(err?.error?.Errors?.message || 'Failed to delete achievements');
        }
      });
    }
  }

  applyPage(): void {
    const start = (this.currentPage() - 1) * this.pageSize;
    this.achievements.set(this.allAchievements().slice(start, start + this.pageSize));
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.totalCount() / this.pageSize);
    this.startIndex = (this.currentPage() - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage() * this.pageSize, this.totalCount());
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage.set(1);
    this.updatePagination();
    this.applyPage();
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update(v => v - 1);
      this.updatePagination();
      this.applyPage();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.update(v => v + 1);
      this.updatePagination();
      this.applyPage();
    }
  }

  openAddModal(): void {
    this.editingAchievement.set(null);
    this.isModalOpen.set(true);
  }

  openEditModal(a: IAdminAchievement): void {
    this.editingAchievement.set(a);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.editingAchievement.set(null);
  }

  onFormSaved(): void {
    this.closeModal();
    this.fetchAchievements();
  }

  async confirmDelete(id: string): Promise<void> {
    const confirmed = await this.confirmationService.confirm({
      header: '',
      bodyHeader: 'Delete achievement',
      bodySubtitle: 'Are you sure you want to delete this achievement? This action cannot be undone.',
      yesButtonText: 'Delete',
      noButtonText: 'Cancel',
      yesBtnCss: 'btn-danger',
      noBtnCss: 'btn-secondary',
      width: '420px',
    });

    if (confirmed) {
      this.achievementService.deleteAchievement(id).subscribe({
        next: (res) => {
          this.toastr.success(res?.data.message || 'Achievement deleted');
          this.fetchAchievements();
        },
        error: (err) => {
          this.toastr.error(err?.error?.Errors?.message || 'Failed to delete achievement');
        }
      });
    }
  }

  trackById(index: number, item: any): string {
    return item._id;
  }

  getBadgeType(badge: string): 'image' | 'icon' | 'emoji' {
    if (!badge) return 'emoji';
    if (badge.startsWith('http') || badge.startsWith('data:') || badge.startsWith('/')) {
      return 'image';
    }
    const emojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;
    if (!emojiRegex.test(badge) && badge.length > 1) {
      return 'icon';
    }
    return 'emoji';
  }

  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === "asc") {
        this.currentSortOrder.set("desc");
      } else if (this.currentSortOrder() === "desc") {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set("asc");
    }
    this.sortAchievementsLocal();
  }

  sortAchievementsLocal(): void {
    const field = this.currentSortField();
    const order = this.currentSortOrder();

    if (!field || !order) {
      this.allAchievements.set([...this.originalAchievements()]);
      this.updatePagination();
      this.applyPage();
      return;
    }

    const direction = order === "asc" ? 1 : -1;
    const sorted = [...this.allAchievements()];

    sorted.sort((a: any, b: any) => {
      let valA: any = a[field];
      let valB: any = b[field];

      if (field === 'badge') {
        valA = (a.badge || '').toLowerCase();
        valB = (b.badge || '').toLowerCase();
      } else if (field === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      } else if (field === 'description') {
        valA = (a.description || '').toLowerCase();
        valB = (b.description || '').toLowerCase();
      } else if (field === 'category') {
        valA = (a.category || '').toLowerCase();
        valB = (b.category || '').toLowerCase();
      } else if (field === 'target') {
        valA = Number(a.target || 0);
        valB = Number(b.target || 0);
      }

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });

    this.allAchievements.set(sorted);
    this.updatePagination();
    this.applyPage();
  }

  getSortIcon(field: string): string {
    if (this.currentSortField() !== field) return "⇅";
    if (this.currentSortOrder() === "asc") return "↑";
    if (this.currentSortOrder() === "desc") return "↓";
    return "⇅";
  }
}